import { QuestaoMetadata } from "utils/masks";

/**
 * Metadados de máscara/validação das seções TEMPLATE (prefixadas), resolvidos no
 * FRONTEND. O backend expõe a seção via detalhe do tipo de requerimento apenas com
 * { enunciado, tipo, obrigatoria, ... } + um discriminador `tipoSecao` ({value,name}),
 * SEM máscara e sem a `chave` semântica persistida. Por isso resolvemos a máscara por
 * convenção: (nome do TipoSecao) + (enunciado normalizado).
 *
 * Orientado a dados: para habilitar uma nova seção template basta o backend adicioná-la
 * ao catálogo/enum e acrescentar uma entrada aqui — a UI passa a aplicar máscaras/labels.
 */

export const TIPO_SECAO_ENDERECO = "endereco";
export const TIPO_SECAO_PROPRIETARIO = "proprietario";
export const TIPO_SECAO_RESPONSAVEL_TECNICO = "responsavel_tecnico";

const norm = (s: string): string => s.trim().toLowerCase();

// Rótulo progressivo de ocorrência por TipoSecao (numeração no comportamento repetível).
const OCCURRENCE_LABEL: Record<string, (n: number) => string> = {
  endereco: (n) => `Endereço ${n}`,
  proprietario: (n) => `Contato – Proprietário ${n}`,
  responsavel_tecnico: (n) => `Contato – Responsável técnico ${n}`
};

/** Rótulo de ocorrência a partir do nome do TipoSecao; fallback = nome da seção + número. */
export const rotuloOcorrenciaSecao = (
  tipoSecaoName: string | null | undefined,
  nomeSecao: string,
  numero: number
): string => {
  const fn = tipoSecaoName ? OCCURRENCE_LABEL[tipoSecaoName] : undefined;
  return fn ? fn(numero) : `${nomeSecao} ${numero}`;
};

const META_CPF_CNPJ: QuestaoMetadata = {
  inputMask: "dynamic",
  maskType: "cpfCnpj",
  acceptOnlyNumbers: true
};
const META_CEP: QuestaoMetadata = {
  inputMask: "00000-000",
  acceptOnlyNumbers: true
};
const META_TELEFONE: QuestaoMetadata = {
  inputMask: "dynamic",
  maskType: "phone",
  acceptOnlyNumbers: true
};
const META_EMAIL: QuestaoMetadata = { inputMask: "email", validation: "email" };
const META_UF: QuestaoMetadata = { type: "uf" };

// (TipoSecao.name) -> (enunciado normalizado: trim + lowercase) -> metadata.
// As chaves batem com os enunciados do catálogo do backend (CatalogoSecoesTemplate.cs).
const META_POR_TIPO: Record<string, Record<string, QuestaoMetadata>> = {
  endereco: {
    cep: META_CEP,
    uf: META_UF
  },
  // Backend: "Nome Completo / Razão Social", "CPF / CNPJ", "Telefone", "E-mail".
  proprietario: {
    "cpf / cnpj": META_CPF_CNPJ,
    "cpf/cnpj": META_CPF_CNPJ,
    telefone: META_TELEFONE,
    "e-mail": META_EMAIL,
    email: META_EMAIL,
    cep: META_CEP,
    uf: META_UF
  },
  // Backend: "Nome", "CPF", "Conselho Profissional", "Registro", "Telefone", "E-mail".
  responsavel_tecnico: {
    cpf: META_CPF_CNPJ,
    "cpf / cnpj": META_CPF_CNPJ,
    "cpf/cnpj": META_CPF_CNPJ,
    telefone: META_TELEFONE,
    "e-mail": META_EMAIL,
    email: META_EMAIL,
    cep: META_CEP,
    uf: META_UF
  }
};

/**
 * Resolve o metadata de máscara/validação de uma questão de seção template.
 * @param tipoSecaoName nome do TipoSecao ("endereco", ...) — undefined em seção manual.
 * @param enunciado enunciado da questão (ex.: "CEP", "UF", "CPF / CNPJ").
 */
export const metaDaQuestaoTemplate = (
  tipoSecaoName: string | null | undefined,
  enunciado: string
): QuestaoMetadata => {
  if (!tipoSecaoName) return undefined;
  const mapa = META_POR_TIPO[tipoSecaoName];
  return mapa ? mapa[norm(enunciado)] : undefined;
};
